/* eslint-disable implicit-arrow-linebreak */
// ** React Imports
import { useContext } from "react";
import { AbilityContext } from "@src/utility/context/Can";
import { subject } from "@casl/ability";
import { CurrentBuildingContext } from "../../contexts/currentBuilding";

/**
 * Return which component to render based on it's data/context
 * @param {Object} item nav menu item
 */
export const resolveVerticalNavMenuItemComponent = (item) => {
  if (item.header) return "VerticalNavMenuSectionHeader";
  if (item.children) return "VerticalNavMenuGroup";
  return "VerticalNavMenuLink";
};

/**
 * Return which component to render based on it's data/context
 * @param {Object} item nav menu item
 */
export const resolveHorizontalNavMenuItemComponent = (item) => {
  if (item.children) return "HorizontalNavMenuGroup";
  return "HorizontalNavMenuLink";
};

/**
 * Check if nav-link is active
 * @param {Object} link nav-link object
 */
export const isNavLinkActive = (link, currentURL, routerProps) => {
  return (
    currentURL === link ||
    (routerProps &&
      routerProps.meta &&
      routerProps.meta.navLink &&
      routerProps.meta.navLink === link)
  );
  // return currentURL === link
};

/**
 * Check if the given item has the given url
 * in one of its children
 *
 * @param item
 * @param activeItem
 */
export const hasActiveChild = (item, currentUrl) => {
  const { children } = item;

  if (!children) {
    return false;
  }

  for (const child of children) {
    if (child.children) {
      if (hasActiveChild(child, currentUrl)) {
        return true;
      }
    }

    // Check if the child has a link and is active
    if (
      child &&
      child.navLink &&
      currentUrl &&
      (child.navLink === currentUrl || currentUrl.includes(child.navLink))
    ) {
      return true;
    }
  }

  return false;
};

/**
 * Check if this is a children
 * of the given item
 *
 * @param children
 * @param openGroup
 * @param currentActiveGroup
 */
export const removeChildren = (children, openGroup, currentActiveGroup) => {
  children.forEach((child) => {
    if (!currentActiveGroup.includes(child.id)) {
      const index = openGroup.indexOf(child.id);
      if (index > -1) openGroup.splice(index, 1);
      if (child.children)
        removeChildren(child.children, openGroup, currentActiveGroup);
    }
  });
};

const canPerformConfiguredAction = (item, ability, target) => {
  if (!item?.resource) return false;
  const actions = Array.isArray(item.action)
    ? item.action
    : [item.action || "read"];
  return actions.some((action) =>
    ability.can(action, target || item.resource)
  );
};

const checkForVisibleChild = (arr, ability, appView, currentBuilding) => {
  const view = appView || currentBuilding?._id || "company";
  return arr.some((i) => {
    if (i.children) {
      return checkForVisibleChild(i.children, ability, appView, currentBuilding);
    } else {
      return (
        canPerformConfiguredAction(i, ability, subject(i.resource, { view })) ||
        canPerformConfiguredAction(i, ability, subject(i.resource, { view: "building" })) ||
        canPerformConfiguredAction(i, ability, subject(i.resource, { view: "company" })) ||
        canPerformConfiguredAction(i, ability, i.resource)
      );
    }
  });
};

export const canViewMenuGroup = (item, abilityParam) => {
  const abilityContext = useContext(AbilityContext);
  const ability = abilityParam || abilityContext;
  const { currentBuilding } = useContext(CurrentBuildingContext) || {};
  const hasAnyVisibleChild =
    item.children && checkForVisibleChild(item.children, ability, null, currentBuilding);

  if (!(item.action && item.resource)) {
    return hasAnyVisibleChild;
  }

  return canPerformConfiguredAction(item, ability) && hasAnyVisibleChild;
};

const hasAbility = (item, appView, abilityParam, currentBuildingParam) => {
  const abilityContext = useContext(AbilityContext);
  const ability = abilityParam || abilityContext;
  const { currentBuilding } = useContext(CurrentBuildingContext) || {};
  const bldg = currentBuildingParam || currentBuilding;
  const view = appView || bldg?._id || "company";

  return (
    canPerformConfiguredAction(item, ability, subject(item.resource, { view })) ||
    canPerformConfiguredAction(item, ability, subject(item.resource, { view: "building" })) ||
    canPerformConfiguredAction(item, ability, subject(item.resource, { view: "company" })) ||
    canPerformConfiguredAction(item, ability, item.resource)
  );
};

export const canViewMenuItem = (item, items, appView, abilityParam, currentBuildingParam) => {
  const abilityContext = useContext(AbilityContext);
  const ability = abilityParam || abilityContext;
  const { currentBuilding } = useContext(CurrentBuildingContext) || {};
  const bldg = currentBuildingParam || currentBuilding;

  if (item.header) {
    let index = items.findIndex((element) => element.header == item.header);

    if (index === -1) {
      return false;
    }

    for (let i = index + 1; i < items.length; i++) {
      if (items[i].header) {
        break;
      }

      if (hasAbility(items[i], appView, ability, bldg)) return true;
    }

    return false;
  }

  return hasAbility(item, appView, ability, bldg);
};
